import { components } from "@libertify/api-types/media";
import {
  extractTextProperties,
  getAvatarShapeBackgroundColor,
  getShapeType,
  getTextAlign,
  isLineShape,
  isValidShapeGraphics,
  processTemplateAttributes,
  processTemplateItems,
  resolveFontAssetIdToBrandKitPlaceholder,
  resolveColorToBrandKitPlaceholder,
  resolveVideoAssetIdToBrandKitPlaceholder,
  isPlaceholderValue,
  getVerticalAlign,
  mapServerColor,
  parseSpeechDuration,
} from "./itemsHelper";
import { nanoid } from "nanoid";
import {
  AvatarShape,
  BodyView,
  LINE_ORIENTATIONS,
  MediaItem,
  Shape,
  TemplateItemWithLayerIndex,
  TemplateState,
  TemplateType,
} from "../types";
import { rgbaToHex } from "utils/";
import {
  DEFAULT_COLOR,
  DEFAULT_HEADER_HEIGHT,
  DEFAULT_HEADER_LAYER_INDEX,
  DEFAULT_HEADER_POSITION,
  DEFAULT_HEADER_SIZE,
  DEFAULT_LOGO_LAYER_INDEX,
  DEFAULT_LOGO_POSITION,
  DEFAULT_LOGO_RELATIVE_HEIGHT,
  DEFAULT_MUSIC_VOLUME,
  DEFAULT_PRESENTER_LAYER_INDEX,
  DEFAULT_PRESENTER_POSITION,
  DEFAULT_PRESENTER_RELATIVE_HEIGHT,
  DEFAULT_PRESENTER_SIZE,
  DEFAULT_SUBTITLE_RELATIVE_FONT_SIZE,
  DEFAULT_SUBTITLES_LAYER_INDEX,
  DEFAULT_SUBTITLES_LINE_HEIGHT,
  DEFAULT_SUBTITLES_POSITION,
  DEFAULT_SUBTITLES_SIZE,
  DEFAULT_TITLE_LAYER_INDEX,
  DEFAULT_TITLE_LINE_HEIGHT,
  DEFAULT_TITLE_POSITION,
  DEFAULT_TITLE_RELATIVE_FONT_SIZE,
  DEFAULT_TITLE_SIZE,
  INTRODUCTION_VIDEO_PLACEHOLDER,
  CLOSING_VIDEO_PLACEHOLDER,
} from "../constants";
import { getPosition } from ".";
import { extractMask } from "./maskHelper";
import { useBrandKit } from "hooks/useBrandKit/";
import {
  ContactResponse,
  MediaAsset,
  PresenterProfileResponse,
} from "pages/media/types/";

export const mapServerTemplateToTemplateState = (
  template: components["schemas"]["MediaTemplate"] | undefined,
  templateType: TemplateType,
  mediaAspectRatio: number,
  commonAssets: {
    brandKitData: ReturnType<typeof useBrandKit>;
    initialPresenter: PresenterProfileResponse | undefined;
    defaultPublicFont?: MediaAsset;
    contact: ContactResponse | undefined;
    presenterRatio: number | undefined;
    logoRatio: number | undefined;
  }
): TemplateState => {
  const {
    brandKitData,
    initialPresenter,
    defaultPublicFont,
    contact,
    presenterRatio,
    logoRatio,
  } = commonAssets;

  const { brandKitPalette, logo: brandKitLogo } = brandKitData;

  if (!template?.items)
    return mapBrandkitTemplate(templateType, mediaAspectRatio, {
      brandKitData,
      initialPresenter,
      defaultPublicFont,
      contact,
      presenterRatio,
      logoRatio,
    });

  const items = template.items.map((item) => ({
    ...item,
    id: nanoid(),
  }));
  const {
    headerRectangle,
    headerBanner,
    bodyRectangle,
    bodyImage,
    title,
    basicSubtitles,
    animatedSubtitles,
    bodyVideo: videoItem,
    avatarShapeItem,
    avatarItem,
    imageItemsArray,
    videoItemsArray,
    logo: logoItem,
    customTextItemsArray,
    shapeItemsArray,
  } = processTemplateItems(items);

  /**
   * Background
   */
  let bodyView: BodyView = "Color";
  if (videoItem?.type === "CustomAssetVideo" && videoItem.asset_id) {
    bodyView = "Video" as const;
  }
  if (
    bodyImage &&
    "asset_id" in bodyImage &&
    templateType !== TemplateType.INTRODUCTION &&
    templateType !== TemplateType.CLOSING
  ) {
    bodyView = "Picture" as const;
  }

  const videoAssetId =
    videoItem?.type === "CustomAssetVideo" ? videoItem.asset_id : undefined;

  const imageAssetId =
    typeof bodyImage?.asset_id === "string" ? bodyImage.asset_id : undefined;

  const bodyRectangleColor =
    bodyRectangle?.type === "Rectangle" && bodyRectangle.color
      ? mapServerColor(bodyRectangle.color)
      : undefined;

  const background = {
    bodyView,
    bodyVideo: undefined,
    bodyVideoAsset: videoAssetId,
    bodyImage: undefined,
    bodyImageAsset: imageAssetId,
    bodyColor: bodyRectangleColor,
  };

  /**
   * Title
   */
  const {
    fontAssetId: titleFontAssetId,
    fontSize: titleFontSize,
    color: titleColor,
    textBackground: titleTextBackground,
    strokeColor: titleStrokeColor,
    lineHeight: titleLineHeight,
  } = extractTextProperties(title?.graphics) ?? {};

  const {
    position: titlePosition,
    width: titleWidth,
    height: titleHeight,
    animations: titleAnimations,
    rotation: titleRotation,
  } = processTemplateAttributes(title?.attributes);

  const titleStyle = {
    fontPath: undefined,
    fontSize: titleFontSize ?? DEFAULT_TITLE_RELATIVE_FONT_SIZE,
    fontAssetId: titleFontAssetId,
    color: titleColor,
    strokeColor: titleStrokeColor,
    backgroundColor: titleTextBackground,
    position: titlePosition
      ? getPosition(
          titlePosition,
          titleWidth ?? DEFAULT_TITLE_SIZE.width,
          titleHeight ?? DEFAULT_TITLE_SIZE.height
        )
      : DEFAULT_TITLE_POSITION,
    size: {
      width: titleWidth ?? DEFAULT_TITLE_SIZE.width,
      height: null,
    },
    animations: titleAnimations,
    rotation: titleRotation ?? undefined,
    textAlign: getTextAlign(title?.graphics),
    lineHeight: titleLineHeight ?? DEFAULT_TITLE_LINE_HEIGHT,
    layerIndex: title?.layerIndex ?? DEFAULT_TITLE_LAYER_INDEX,
    showTitle: !!titleFontSize,
  };

  /**
   * Subtitles
   */
  const {
    fontAssetId: subtitlesFontAssetId,
    fontSize: subtitlesFontSize,
    color: subtitlesColor,
    strokeColor: subtitlesStrokeColor,
    textBackground: subtitlesTextBackground,
    lineHeight: subtitlesLineHeight,
  } = extractTextProperties(
    basicSubtitles?.graphics ?? animatedSubtitles?.graphics
  ) ?? {};

  const {
    position: subtitlesPosition,
    width: subtitlesWidth,
    height: subtitlesHeight,
    animations: subtitlesAnimations,
    rotation: subtitlesRotation,
  } = processTemplateAttributes(
    basicSubtitles?.attributes ?? animatedSubtitles?.attributes
  );

  const textStyle = {
    fontPath: undefined,
    fontAssetId: subtitlesFontAssetId,
    fontSize: subtitlesFontSize ?? DEFAULT_SUBTITLE_RELATIVE_FONT_SIZE,
    color: subtitlesColor,
    strokeColor: subtitlesStrokeColor,
    backgroundColor: subtitlesTextBackground,
    position: subtitlesPosition
      ? getPosition(
          subtitlesPosition,
          subtitlesWidth ?? DEFAULT_SUBTITLES_SIZE.width,
          subtitlesHeight ?? DEFAULT_SUBTITLES_SIZE.height
        )
      : DEFAULT_SUBTITLES_POSITION,
    size: {
      width: subtitlesWidth ?? DEFAULT_SUBTITLES_SIZE.width,
      height: null,
    },
    animations: subtitlesAnimations,
    rotation: subtitlesRotation ?? undefined,
    showSubtitles: !!subtitlesFontSize,
    textAlign: "center" as const,
    animated: !!animatedSubtitles,
    lineHeight: subtitlesLineHeight ?? DEFAULT_SUBTITLES_LINE_HEIGHT,
    layerIndex:
      basicSubtitles?.layerIndex ??
      animatedSubtitles?.layerIndex ??
      DEFAULT_SUBTITLES_LAYER_INDEX,
  };

  /**
   * Header
   */
  const {
    position: headerPosition,
    width: headerWidth,
    height: headerHeight,
  } = processTemplateAttributes((headerRectangle ?? headerBanner)?.attributes);

  const headerRectangleColor =
    headerRectangle?.graphics.type === "Rectangle" &&
    headerRectangle.graphics.color
      ? mapServerColor(headerRectangle.graphics.color)
      : resolveColorToBrandKitPlaceholder(
          brandKitPalette?.secondary_color ?? undefined,
          brandKitPalette
        );

  const header = {
    view: headerBanner ? "Picture" : "Color",
    position: headerPosition
      ? getPosition(
          headerPosition,
          headerWidth ?? DEFAULT_HEADER_SIZE.width,
          headerHeight ?? DEFAULT_HEADER_SIZE.height
        )
      : DEFAULT_HEADER_POSITION,
    size:
      headerWidth && headerHeight
        ? {
            width: headerWidth,
            height: headerHeight,
          }
        : DEFAULT_HEADER_SIZE,
    background: headerRectangleColor,
    banner: headerBanner
      ? {
          assetId:
            headerBanner.graphics.type === "AssetImage" &&
            typeof headerBanner.graphics.asset_id === "string"
              ? headerBanner.graphics.asset_id
              : undefined,
        }
      : undefined,
    layerIndex:
      (headerRectangle ?? headerBanner)?.layerIndex ??
      DEFAULT_HEADER_LAYER_INDEX,
  };

  /**
   * Logo
   */
  const {
    position: logoPositionWithinHeader,
    width: logoWidth,
    height: logoHeight,
    rotation: logoRotation,
  } = processTemplateAttributes(logoItem?.attributes);
  const logo = {
    showLogo: !!logoItem,
    rotation: logoRotation ?? undefined,
    assetId: logoItem ? brandKitLogo?.asset_id : null,
    path: brandKitLogo?.path,
    position: logoPositionWithinHeader
      ? getPosition(
          logoPositionWithinHeader,
          logoWidth ?? DEFAULT_LOGO_RELATIVE_HEIGHT * (logoRatio ?? 1),
          logoHeight ?? DEFAULT_LOGO_RELATIVE_HEIGHT
        )
      : DEFAULT_LOGO_POSITION,
    size:
      logoWidth && logoHeight
        ? { width: logoWidth, height: logoHeight }
        : {
            width: DEFAULT_LOGO_RELATIVE_HEIGHT * (logoRatio ?? 1),
            height: DEFAULT_LOGO_RELATIVE_HEIGHT,
          },
    layerIndex: logoItem?.layerIndex ?? DEFAULT_LOGO_LAYER_INDEX,
  };

  /**
   * Presenter
   */
  const avatar = avatarShapeItem
    ? avatarShapeItem.attributes
    : avatarItem?.attributes;

  const {
    position: presenterPosition,
    height: presenterHeight,
    width: presenterWidth,
    animations: presenterAnimations,
    rotation: presenterRotation,
  } = processTemplateAttributes(avatar);

  const avatarShapeGraphics = avatarShapeItem?.graphics;
  const avatarShapeBackgroundColor =
    getAvatarShapeBackgroundColor(avatarShapeGraphics);

  let avatarShape: AvatarShape = AvatarShape.Regular;
  if (avatarShapeItem?.graphics.type === "Circle") {
    avatarShape = AvatarShape.Circle;
  } else if (
    avatarShapeItem?.graphics.type === "Rectangle" &&
    avatarShapeBackgroundColor
  ) {
    avatarShape = AvatarShape.Box;
  }
  const avatarStroke =
    avatarShapeGraphics &&
    "border" in avatarShapeGraphics &&
    avatarShapeGraphics.border
      ? {
          width: avatarShapeGraphics.border.thickness,
          color: rgbaToHex(avatarShapeGraphics.border.color),
          radius: avatarShapeGraphics.border.radius,
        }
      : undefined;

  const presenter = {
    presenter: avatar ? initialPresenter : undefined,
    contactId: avatar ? contact?.id : undefined,
    position: presenterPosition
      ? getPosition(
          presenterPosition,
          presenterWidth ?? DEFAULT_PRESENTER_SIZE.width,
          presenterHeight ?? DEFAULT_PRESENTER_SIZE.height
        )
      : DEFAULT_PRESENTER_POSITION,
    size: {
      width:
        avatarShape === AvatarShape.Regular
          ? (presenterWidth ?? DEFAULT_PRESENTER_SIZE.width)
          : (presenterHeight ?? DEFAULT_PRESENTER_SIZE.height),
      height: presenterHeight ?? DEFAULT_PRESENTER_SIZE.height,
    },
    animations: presenterAnimations,
    rotation: presenterRotation ?? undefined,
    avatarShape,
    avatarShot: template.avatar_shot ?? "Medium",
    background: avatarShapeBackgroundColor,
    stroke: avatarStroke,
    layerIndex: avatarItem?.layerIndex ?? DEFAULT_PRESENTER_LAYER_INDEX,
  };

  /**
   * Images
   */
  const images = mapMediaItems(imageItemsArray);

  /**
   * Videos
   */
  const videos = mapMediaItems(videoItemsArray);

  /**
   * Music
   */
  // A template saved with video sound on stores muted music; restore the default
  // so turning the sound off does not leave the music silent.
  const hasVideoSound =
    (templateType === TemplateType.INTRODUCTION ||
      templateType === TemplateType.CLOSING) &&
    template.speech?.type === "SoundEffect";
  const music = hasVideoSound
    ? { volume: DEFAULT_MUSIC_VOLUME }
    : (template.music ?? { volume: DEFAULT_MUSIC_VOLUME });

  /**
   * Custom Texts
   */
  const customTexts = customTextItemsArray.flatMap((customText) => {
    const textAlign = getTextAlign(customText.graphics);
    const verticalAlign = getVerticalAlign(customText.graphics);
    const { position, width, height, animations, rotation } =
      processTemplateAttributes(customText.attributes);
    const {
      fontAssetId: customTextFontAssetId,
      fontSize: customTextFontSize,
      color: customTextColor,
      textBackground: customTextTextBackground,
      content: customTextContent,
      strokeColor: customTextStrokeColor,
      lineHeight: customTextLineHeight,
    } = extractTextProperties(customText.graphics) ?? {};

    if (
      !width ||
      !height ||
      !position ||
      !customTextContent ||
      !customTextFontSize ||
      !customTextFontAssetId ||
      !customTextColor
    )
      return [];

    return [
      {
        id: customText.name ?? nanoid(),
        content: customTextContent,
        fontSize: customTextFontSize,
        fontAssetId: customTextFontAssetId,
        color: customTextColor,
        strokeColor: customTextStrokeColor,
        backgroundColor: customTextTextBackground,
        position: getPosition(position, width, height),
        size: { width, height },
        animations,
        rotation: rotation ?? undefined,
        textAlign,
        verticalAlign,
        lineHeight: customTextLineHeight ?? DEFAULT_TITLE_LINE_HEIGHT,
        layerIndex: customText.layerIndex,
      },
    ];
  });

  /**
   * Shapes
   */
  const shapes = shapeItemsArray.flatMap((shape): Shape[] => {
    if (!isValidShapeGraphics(shape.graphics)) return [];
    const { position, width, height, animations, rotation } =
      processTemplateAttributes(shape.attributes);
    if (!position || !shape.name) return [];

    if (isLineShape(shape.graphics)) {
      if (!width && !height) return [];
      const size = {
        width:
          shape.graphics.orientation === LINE_ORIENTATIONS.HORIZONTAL
            ? width
            : shape.graphics.thickness,
        height:
          shape.graphics.orientation === LINE_ORIENTATIONS.HORIZONTAL
            ? shape.graphics.thickness
            : height,
      };

      if (!size.width || !size.height) return [];

      // Adjust position from center (server) to top-left (preview)
      const rawPosition = getPosition(position, size.width, size.height);
      const adjustedPosition = {
        x:
          shape.graphics.orientation === LINE_ORIENTATIONS.VERTICAL
            ? rawPosition.x - shape.graphics.thickness / 2
            : rawPosition.x,
        y:
          shape.graphics.orientation === LINE_ORIENTATIONS.HORIZONTAL
            ? rawPosition.y - shape.graphics.thickness / 2
            : rawPosition.y,
      };

      return [
        {
          id: shape.name,
          shapeType: getShapeType(shape.graphics.type),
          size: { width: size.width, height: size.height },
          position: adjustedPosition,
          background: mapServerColor(shape.graphics.color),
          animations,
          rotation: rotation ?? undefined,
          layerIndex: shape.layerIndex,
          stroke: undefined,
          orientation: shape.graphics.orientation,
        },
      ];
    }

    if (!width || !height) return [];

    return [
      {
        id: shape.name,
        shapeType: getShapeType(shape.graphics.type),
        size: { width, height },
        position: getPosition(position, width, height),
        background: mapServerColor(shape.graphics.color),
        animations,
        rotation: rotation ?? undefined,
        layerIndex: shape.layerIndex,
        stroke: shape.graphics.border
          ? {
              width: shape.graphics.border.thickness,
              color: rgbaToHex(shape.graphics.border.color),
              radius: shape.graphics.border.radius,
            }
          : undefined,
        orientation: undefined,
      },
    ];
  });

  /**
   * Transition / Intro / Closing
   */
  const { intro: brandKitIntro, closing: brandKitClosing } = brandKitData;

  const speechDuration = parseSpeechDuration(template);

  // Convert asset ID to placeholder if it matches brand kit, and resolve path
  const getIntroClosingAsset = (
    assetId: string | undefined
  ): { assetId: string | undefined; assetPath: string | undefined } => {
    if (!assetId) return { assetId: undefined, assetPath: undefined };

    // If already a placeholder, resolve path from brand kit
    if (isPlaceholderValue(assetId)) {
      if (assetId === INTRODUCTION_VIDEO_PLACEHOLDER) {
        return { assetId, assetPath: brandKitIntro?.path };
      }
      if (assetId === CLOSING_VIDEO_PLACEHOLDER) {
        return { assetId, assetPath: brandKitClosing?.path };
      }
      return { assetId, assetPath: undefined };
    }

    // Convert actual asset ID to placeholder if it matches brand kit
    const resolvedAssetId = resolveVideoAssetIdToBrandKitPlaceholder(
      assetId,
      brandKitIntro,
      brandKitClosing
    );

    // If converted to placeholder, get path from brand kit
    if (resolvedAssetId === INTRODUCTION_VIDEO_PLACEHOLDER) {
      return { assetId: resolvedAssetId, assetPath: brandKitIntro?.path };
    }
    if (resolvedAssetId === CLOSING_VIDEO_PLACEHOLDER) {
      return { assetId: resolvedAssetId, assetPath: brandKitClosing?.path };
    }

    // Not a brand kit asset, keep actual ID (path will be fetched later)
    return { assetId: resolvedAssetId, assetPath: undefined };
  };

  const transition =
    templateType === TemplateType.TRANSITION
      ? {
          enable: true,
          assetId: videoAssetId ?? imageAssetId,
          assetPath: undefined,
          view: getBodyView(videoAssetId, imageAssetId),
          duration: speechDuration,
          speechType: template.speech?.type,
        }
      : {
          enable: false,
          view: "Color" as const,
        };

  const introAsset = getIntroClosingAsset(videoAssetId);
  const intro =
    templateType === TemplateType.INTRODUCTION
      ? {
          enable: true,
          assetId: introAsset.assetId,
          assetPath: introAsset.assetPath,
          view: "Video" as const,
          duration: speechDuration,
          playVideoSound: template.speech?.type === "SoundEffect",
        }
      : {
          enable: false,
          view: "Video" as const,
        };

  const closingAsset = getIntroClosingAsset(videoAssetId);
  const closing =
    templateType === TemplateType.CLOSING
      ? {
          enable: true,
          assetId: closingAsset.assetId,
          assetPath: closingAsset.assetPath,
          view: "Video" as const,
          duration: speechDuration,
          playVideoSound: template.speech?.type === "SoundEffect",
        }
      : {
          enable: false,
          view: "Video" as const,
        };

  return {
    background,
    titleStyle,
    textStyle,
    header,
    logo,
    presenter,
    images,
    videos,
    music,
    customTexts,
    shapes,
    transition,
    intro,
    closing,
  };
};

const mapMediaItems = (items: TemplateItemWithLayerIndex[]): MediaItem[] =>
  items.flatMap((item) => {
    const itemGraphics = item.graphics;
    const itemGraphicsId =
      itemGraphics.type === "AssetImage" ||
      itemGraphics.type === "CustomAssetVideo"
        ? itemGraphics.asset_id
        : undefined;

    if (!itemGraphicsId || typeof itemGraphicsId !== "string") return [];

    const { width, height, animations, position, rotation } =
      processTemplateAttributes(item.attributes);

    if (!width || !height || !position) return [];

    const stroke =
      "border" in item.graphics && item.graphics.border
        ? {
            width: item.graphics.border.thickness,
            color: rgbaToHex(item.graphics.border.color),
            radius: item.graphics.border.radius,
          }
        : undefined;

    const mask = extractMask(itemGraphics);

    return [
      {
        id: item.name ?? nanoid(),
        path: undefined,
        assetId: itemGraphicsId,
        position: getPosition(position, width, height),
        size: { width, height },
        animations: animations,
        rotation: rotation ?? undefined,
        stroke: stroke,
        layerIndex: item.layerIndex,
        ...(mask ? { mask } : {}),
      },
    ];
  });

const getBodyView = (
  videoAssetId: string | undefined,
  imageAssetId: string | undefined
): BodyView => {
  if (videoAssetId) return "Video";
  if (imageAssetId) return "Picture";
  return "Color";
};

export const mapBrandkitTemplate = (
  templateType: TemplateType,
  mediaAspectRatio: number,
  templateData: {
    brandKitData: ReturnType<typeof useBrandKit>;
    initialPresenter: PresenterProfileResponse | undefined;
    defaultPublicFont: MediaAsset | undefined;
    contact: ContactResponse | undefined;
    presenterRatio: number | undefined;
    logoRatio: number | undefined;
  }
): TemplateState => {
  const {
    brandKitData,
    initialPresenter,
    defaultPublicFont,
    contact,
    presenterRatio,
    logoRatio,
  } = templateData;
  const {
    brandKitPalette,
    logo: brandKitLogo,
    titleFont: brandKitTitleFont,
    subtitlesFont: brandKitSubtitlesFont,
    textFont: brandKitTextFont,
    banner: brandKitBanner,
    intro: brandKitIntro,
    closing: brandKitClosing,
    transition: brandKitTransition,
    backgroundBrandKitPicture,
    backgroundBrandKitVideo,
  } = brandKitData;

  const brandKitFonts = [
    brandKitTitleFont,
    brandKitSubtitlesFont,
    brandKitTextFont,
  ].filter((font) => !!font);

  const defaultTextStyleObject = {
    fontPath: brandKitSubtitlesFont?.path ?? defaultPublicFont?.path,
    color: resolveColorToBrandKitPlaceholder(
      brandKitPalette?.subtitle_color ?? undefined,
      brandKitPalette
    ),
    strokeColor: brandKitPalette?.subtitle_stroke_color
      ? resolveColorToBrandKitPlaceholder(
          brandKitPalette.subtitle_stroke_color,
          brandKitPalette
        )
      : undefined,
    fontAssetId: resolveFontAssetIdToBrandKitPlaceholder(
      brandKitSubtitlesFont?.asset_id ?? defaultPublicFont?.id,
      brandKitFonts
    ),
    fontSize: DEFAULT_SUBTITLE_RELATIVE_FONT_SIZE,
    textAlign: "center" as const,
    lineHeight: DEFAULT_SUBTITLES_LINE_HEIGHT,
    showSubtitles: true,
    animated: true,
    position: DEFAULT_SUBTITLES_POSITION,
    size: {
      width: DEFAULT_SUBTITLES_SIZE.width,
      height: null,
    },
    layerIndex: DEFAULT_SUBTITLES_LAYER_INDEX,
  };

  const defaultTitleStyleObject = {
    fontPath: brandKitTitleFont?.path ?? defaultPublicFont?.path,
    color: resolveColorToBrandKitPlaceholder(
      brandKitPalette?.title_color ?? undefined,
      brandKitPalette
    ),
    fontAssetId: resolveFontAssetIdToBrandKitPlaceholder(
      brandKitTitleFont?.asset_id ?? defaultPublicFont?.id,
      brandKitFonts
    ),
    fontSize: DEFAULT_TITLE_RELATIVE_FONT_SIZE,
    textAlign: "center" as const,
    lineHeight: DEFAULT_TITLE_LINE_HEIGHT,
    position: DEFAULT_TITLE_POSITION,
    size: {
      width: DEFAULT_TITLE_SIZE.width,
      height: null,
    },
    showTitle: true,
    layerIndex: DEFAULT_TITLE_LAYER_INDEX,
  };

  const bodyView: BodyView =
    ((backgroundBrandKitVideo ?? backgroundBrandKitPicture)?.type as
      | undefined
      | "Picture"
      | "Video") ?? "Color";
  const backgroundImage = backgroundBrandKitPicture?.path;
  const backgroundVideo = backgroundBrandKitVideo?.path;
  const hasMediaBackground = !!backgroundImage || !!backgroundVideo;
  const defaultBackgroundObject = {
    bodyView,
    bodyImage: backgroundImage,
    bodyImageAsset: backgroundBrandKitPicture?.asset_id,
    bodyVideo: backgroundVideo,
    bodyVideoAsset: backgroundBrandKitVideo?.asset_id,
    bodyColor: hasMediaBackground
      ? undefined
      : resolveColorToBrandKitPlaceholder(
          brandKitPalette?.primary_color,
          brandKitPalette
        ),
  };

  let logoHeight = DEFAULT_LOGO_RELATIVE_HEIGHT;
  let logoWidth = (logoHeight * (logoRatio ?? 1)) / mediaAspectRatio;
  if (logoWidth > 1) {
    logoWidth = 1;
    logoHeight = mediaAspectRatio / (logoRatio ?? 1);
  }
  const logoY = (DEFAULT_HEADER_HEIGHT - DEFAULT_LOGO_RELATIVE_HEIGHT) / 2;
  const logoX = 0.5 - logoWidth / 2;

  const defaultLogoObject = {
    showLogo: !!(brandKitLogo && logoRatio),
    assetId: brandKitLogo?.asset_id,
    path: brandKitLogo?.path,
    size: { height: logoHeight, width: logoWidth },
    position: { x: logoX, y: logoY },
    layerIndex: DEFAULT_LOGO_LAYER_INDEX,
  };

  const defaultHeaderObject = {
    view: brandKitBanner ? "Picture" : ("Color" as const),
    position: DEFAULT_HEADER_POSITION,
    size: {
      width: 1,
      height: DEFAULT_HEADER_HEIGHT,
    },
    background: brandKitPalette?.secondary_color
      ? resolveColorToBrandKitPlaceholder(
          brandKitPalette.secondary_color,
          brandKitPalette
        )
      : undefined,
    banner: brandKitBanner
      ? {
          assetId: brandKitBanner.asset_id,
          path: brandKitBanner.path,
        }
      : undefined,
    layerIndex: DEFAULT_HEADER_LAYER_INDEX,
  };

  let presenterHeight = DEFAULT_PRESENTER_RELATIVE_HEIGHT;
  let presenterWidth =
    (presenterHeight * (presenterRatio ?? 1)) / mediaAspectRatio;
  if (presenterWidth > 1) {
    presenterWidth = 1;
    presenterHeight = mediaAspectRatio / (presenterRatio ?? 1);
  }
  const presenterX = 0.5 - presenterWidth / 2;
  const presenterY = 1 - presenterHeight;

  const defaultPresenterObject = {
    avatarShape: AvatarShape.Regular,
    avatarShot: "Medium" as const,
    presenter: initialPresenter ?? undefined,
    contactId: contact?.id ?? undefined,
    size: {
      height: presenterHeight,
      width: presenterWidth,
    },
    position: {
      x: presenterX,
      y: presenterY,
    },
    stroke: undefined,
    animations: undefined,
    background: undefined,
    layerIndex: DEFAULT_PRESENTER_LAYER_INDEX,
  };

  const defaultIntroObject = {
    enable: !!brandKitIntro,
    assetPath: brandKitIntro?.path,
    // Store as placeholder if from brand kit, allowing dynamic resolution
    assetId: brandKitIntro ? INTRODUCTION_VIDEO_PLACEHOLDER : undefined,
    view: "Video" as const,
  };

  const defaultClosingObject = {
    enable: !!brandKitClosing,
    assetPath: brandKitClosing?.path,
    // Store as placeholder if from brand kit, allowing dynamic resolution
    assetId: brandKitClosing ? CLOSING_VIDEO_PLACEHOLDER : undefined,
    view: "Video" as const,
  };

  const defaultTransitionObject = {
    enable: !!brandKitTransition,
    assetPath: brandKitTransition?.path,
    assetId: brandKitTransition?.asset_id,
    view: brandKitTransition?.path ? ("Video" as const) : ("Color" as const),
  };

  if (templateType === TemplateType.TRANSITION) {
    defaultBackgroundObject.bodyColor = defaultTransitionObject.assetId
      ? undefined
      : resolveColorToBrandKitPlaceholder(
          brandKitPalette?.primary_color ?? DEFAULT_COLOR,
          brandKitPalette
        );
  }

  let introOrClosingAsset: typeof brandKitIntro | null = null;
  let introOrClosingPlaceholder: string | undefined;
  if (templateType === TemplateType.INTRODUCTION) {
    introOrClosingAsset = brandKitIntro;
    introOrClosingPlaceholder = INTRODUCTION_VIDEO_PLACEHOLDER;
  } else if (templateType === TemplateType.CLOSING) {
    introOrClosingAsset = brandKitClosing;
    introOrClosingPlaceholder = CLOSING_VIDEO_PLACEHOLDER;
  }

  if (introOrClosingAsset) {
    defaultBackgroundObject.bodyView = "Video";
    defaultBackgroundObject.bodyVideo = introOrClosingAsset.path;
    // Store as placeholder for dynamic resolution when brand kit changes
    defaultBackgroundObject.bodyVideoAsset = introOrClosingPlaceholder;
    defaultBackgroundObject.bodyColor = undefined;
  }

  const isIntroOrClosing =
    templateType === TemplateType.INTRODUCTION ||
    templateType === TemplateType.CLOSING;

  // Chapters are the only scene type that should default to the account's
  // presenter; transitions (like intro/closing) start with no avatar until
  // the user explicitly enables one for that transition.
  const defaultsToNoPresenter =
    isIntroOrClosing || templateType === TemplateType.TRANSITION;

  return {
    background: defaultBackgroundObject,
    titleStyle: {
      ...defaultTitleStyleObject,
      showTitle: isIntroOrClosing ? false : defaultTitleStyleObject.showTitle,
    },
    textStyle: {
      ...defaultTextStyleObject,
      showSubtitles: isIntroOrClosing
        ? false
        : defaultTextStyleObject.showSubtitles,
    },
    header: defaultHeaderObject,
    logo: defaultLogoObject,
    presenter: {
      ...defaultPresenterObject,
      presenter: defaultsToNoPresenter
        ? undefined
        : defaultPresenterObject.presenter,
    },
    intro: defaultIntroObject,
    closing: defaultClosingObject,
    transition: defaultTransitionObject,
    images: [],
    videos: [],
    customTexts: [],
    shapes: [],
  };
};
